import MenuItem from '@mui/material/MenuItem';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import makeStyles from '@mui/styles/makeStyles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PageviewOutlinedIcon from '@mui/icons-material/PageviewOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import clsx from 'clsx';
import { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { GET_SIGNED_URL_DOWNLOAD_COMPONENT, GET_SIGNED_URL_DOWNLOAD_EXTRACTED_COMPONENT } from '../../graphql/queries/asset';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import NRAssetsActionsStyles from './NRAssetsActions.styles';
import useNRLazyQuery from '../../graphql/useNRLazyQuery';
import NRAnalysisLog from '../../components/NRAnalysisLog/NRAnalysisLog';
import CustomError from '../../components/CustomError/CustomError';
import { AssetsContext } from '../../DataShare/NRAssetsDataShare';
import { NRMenu } from '../../components/NRMenu/NRMenu';
import { CONSTANTS } from '../../constants/constants';
import WithPermissions from '../../components/Permissions/WithPermissions';
import { ACR_RESOURCE_TYPE } from '../../pages/NRSettings/NRUsersAndAccess/rbac.constants';
import { RBAC_PERMISSIONS } from '../../pages/NRSettings/NRUsersAndAccess/NRRoles/NRCustomRoles/NRCustomRolePermissions.constants';
import { getDataFromTurbinePPComposedAssetId } from '../../utils/utils';
import NRGroupIcon from '../../NRIcons/NRGroupIcon';
import * as React from 'react';
import { AssetGroupContext } from '../../components/AssetGroup/assetGroupContextWrapper';
import Typography from '@mui/material/Typography';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import Divider from '@mui/material/Divider';
import { useLocation } from 'react-router-dom';
import { removeTabAndSubTabFromFilePath } from '../../services/urlInterceptor.utils';
import RemoveFromGroupModal from '../../components/AssetGroup/RemoveFromGroupModal/RemoveFromGroupModal';
import { encodeParam } from '../../services/params.utils';
import { createRiseAIPdfTaskParams } from '../../utils/riseAiPdfTask';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../constants/constants';
import NRActionModal from '../../components/NRActionModal/NRActionModal';
import PDFReportSelectionModal from '../../components/PDFReportSelectionModal/PDFReportSelectionModal';
import { GET_RISE_AI_AVAILABILITY } from '../../graphql/queries/riseAI';
import { RISE_AI_STATUS } from '../../components/NRRiseAiChat/constants';
import NRAssetSummaryPrintableContainer from '../../components/NRAssetSummary/NRAssetSummaryPrintableContainer';
const useStyles = makeStyles(NRAssetsActionsStyles);

function NRAssetsActions({ component, anchorEl, setAnchorEl, onMenuClose, handleEditClick, assetGroupId }) {
  const [analysisLogModalJobID, setAnalysisLogModalJobID] = useState(null);
  const [downloadTask, setDownloadTask] = useState(null);
  const [pdfReportModalOpen, setPdfReportModalOpen] = useState(false);
  const [isAssetSummarySelected, setIsAssetSummarySelected] = useState(false);
  const [isPQCReadinessSelected, setIsPQCReadinessSelected] = useState(false);
  const [isRiseAISelected, setIsRiseAISelected] = useState(false);
  const open = Boolean(anchorEl);
  const classes = useStyles();
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [componentId, setComponentId] = useState(null);
  const [assetSummaryGenerateToken, setAssetSummaryGenerateToken] = useState(null);
  const effectiveComponent = component ?? selectedComponent;
  const { enqueueSnackbar } = useSnackbar();
  const { tasks, setTasks } = useContext(AssetsContext);
  const { setShowManageGroupsModal, setGroupsModalContent, setSelectedAsset, setDisableEdit } = useContext(AssetGroupContext);
  const location = useLocation();
  const disableActions = effectiveComponent?.row?.status === CONSTANTS.TABLE.ASSETS.STATUS.IN_PROGRESS;
  const assetResourceId = componentId ? getDataFromTurbinePPComposedAssetId(componentId)?.assetId : undefined;
  const { value: isRiseAIInsightsReportEnabled } = useFlag(FEATURE_FLAGS.RISE_AI_INSIGHTS_REPORT_SETTINGS, false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [caasAvailability, setCaasAvailability] = useState(false);
  const previousComponentIdRef = useRef(null);
  const [caasAvailabilityData, caasAvailabilityLoading, caasCalled, makeCaaSAvailabilityRequest] = useNRLazyQuery({
    query: GET_RISE_AI_AVAILABILITY,
    options: {
      context: { version: 'v3' },
      fetchPolicy: 'no-cache'
    },
    qlObjKey: 'riseAIAvailability',
    defaultValue: {
      available: false,
      eligible: false,
      status: RISE_AI_STATUS.UNSPECIFIED
    }
  });

  useEffect(() => {
    if (component) {
      setSelectedComponent(component);
      setComponentId(component?.id || component?.componentId);
    }
  }, [component]);

  useEffect(() => {
    if (previousComponentIdRef.current !== componentId) {
      previousComponentIdRef.current = componentId;
      setCaasAvailability(false);

      if (isRiseAIInsightsReportEnabled && componentId) {
        const requestedComponentId = componentId;
        makeCaaSAvailabilityRequest({
          variables: {
            args: {
              assetId: componentId
            }
          },
          context: { version: 'v3' },
          onCompleted: data => {
            if (requestedComponentId !== previousComponentIdRef.current) {
              return;
            }
            setCaasAvailability(data?.riseAIAvailability?.available ?? false);
          }
        });
      }
    }
  }, [componentId, isRiseAIInsightsReportEnabled, component, makeCaaSAvailabilityRequest]);

  const handleMenuClose = () => {
    setAnchorEl(null);
    !!onMenuClose && onMenuClose();
  };

  const handlePivotToSearch = () => {
    handleMenuClose();
    let extraParams = new URLSearchParams();
    extraParams.append('asset', encodeParam([componentId]));
    window.open(`/search/Search?${extraParams.toString()}`, '_blank');
  };

  const [assetDownload, isAssetDownloadLoading, , getAssetDownloadOperationId] = useNRLazyQuery({
    query: GET_SIGNED_URL_DOWNLOAD_COMPONENT,
    qlObjKey: 'download',
    options: { fetchPolicy: 'no-cache', context: { version: 'v3' } },
    defaultValue: '',
    transformData: data => {
      return data?.firmware?.downloadUrlsList;
    }
  });

  const [extractedAssetDownload, isExtractedAssetDownloadLoading, , getExtractedAssetDownloadOperationId] = useNRLazyQuery({
    query: GET_SIGNED_URL_DOWNLOAD_EXTRACTED_COMPONENT,
    qlObjKey: 'download',
    options: { fetchPolicy: 'no-cache', context: { version: 'v3' } },
    defaultValue: '',
    transformData: data => {
      return data?.extractedFirmware?.downloadUrlsList;
    }
  });

  function handleViewAnalysisLogClick() {
    !!componentId && setAnalysisLogModalJobID(componentId);
    handleMenuClose();
  }

  function handlePdfGeneration() {
    handleMenuClose();
    setIsAssetSummarySelected(false);
    setIsPQCReadinessSelected(false);
    setIsRiseAISelected(false);
    setPdfReportModalOpen(true);
  }

  const handleAssetSummaryToggle = () => {
    setIsAssetSummarySelected(prev => !prev);
  };

  const handlePQCReadinessToggle = () => {
    setIsPQCReadinessSelected(prev => !prev);
  };

  const handleRiseAIToggle = () => {
    setIsRiseAISelected(prev => !prev);
  };

  const handlePdfReportModalClose = useCallback(() => {
    setPdfReportModalOpen(false);
  }, []);

  // Resolved asset display name. Used both when generating the PDF (with an
  // 'asset' fallback) and when forwarding to the printable container (kept as
  // undefined when missing, matching the previous behavior).
  const assetName = useMemo(
    () => effectiveComponent?.row?.name || effectiveComponent?.name || effectiveComponent?.component?.name,
    [effectiveComponent]
  );

  const handlePdfReportGenerate = useCallback(async () => {
    if (!componentId) {
      enqueueSnackbar('Unable to generate PDF: Asset ID is missing.', {
        variant: 'error'
      });
      return;
    }

    const formattedAssetName = (assetName || 'asset').replace(/\s+/g, '_');

    try {
      // Only the asset-summary branch drives `isGenerating`. Its work runs
      // asynchronously inside NRAssetSummaryPrintableContainer (data fetch +
      // react-to-print), and the modal/menu-item spinner stay active until
      // `onComplete`/`onError` fires on this component. PQC and RiseAI just
      // enqueue a task into the global tasks queue (tracked by a separate UI)
      // and close this modal synchronously, so a spinner here would never be
      // visible to the user and would only flicker.
      if (isAssetSummarySelected) {
        setIsGenerating(true);
        setAssetSummaryGenerateToken(uuidv4());
      }

      if (isPQCReadinessSelected) {
        const taskParams = {
          id: uuidv4(),
          type: CONSTANTS.TASKS.TYPE.DOWNLOAD_HTTP_POST,
          status: CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.CREATED,
          fileName: `${formattedAssetName}-PQC-Audit-Report.pdf`,
          downloadUrl: 'generate-pdf/pqc',
          body: {
            assetId: componentId,
            baseUrl: window.location.origin,
            pathname: removeTabAndSubTabFromFilePath(location)
          },
          headers: {},
          onSuccess: () => {
            if (!isAssetSummarySelected && !isRiseAISelected) {
              handlePdfReportModalClose();
            }
          },
          onError: () => {},
          errorMessage: 'Failed to generate PQC Readiness PDF. Please try again.'
        };
        setTasks(prev => [taskParams, ...prev]);
        if (!isAssetSummarySelected && !isRiseAISelected) {
          handlePdfReportModalClose();
        }
      }

      if (isRiseAISelected && isRiseAIInsightsReportEnabled) {
        const taskParams = createRiseAIPdfTaskParams(componentId, formattedAssetName, {
          onSuccess: () => {
            if (!isAssetSummarySelected && !isPQCReadinessSelected) {
              handlePdfReportModalClose();
            }
          }
        });
        setTasks(prev => [taskParams, ...prev]);
        if (!isAssetSummarySelected) {
          handlePdfReportModalClose();
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate PDF. Please try again later.', {
        variant: 'error'
      });
      // If the synchronous setup blew up, recover the asset-summary state so
      // the modal/spinner aren't left stuck. The container's own onError flow
      // already covers async failures from the data fetch / print pipeline.
      if (isAssetSummarySelected) {
        setIsGenerating(false);
        setAssetSummaryGenerateToken(null);
      }
      handlePdfReportModalClose();
    }
  }, [
    componentId,
    assetName,
    isAssetSummarySelected,
    isPQCReadinessSelected,
    isRiseAISelected,
    isRiseAIInsightsReportEnabled,
    enqueueSnackbar,
    location,
    setTasks,
    handlePdfReportModalClose
  ]);

  const hasSelectedReports = isAssetSummarySelected || isRiseAISelected || isPQCReadinessSelected;
  const mightHaveRiseAI = isRiseAIInsightsReportEnabled && (caasAvailabilityLoading || caasAvailability);
  const availableReportsCount = 2 + (mightHaveRiseAI ? 1 : 0);
  const pdfReportText = `Generate PDF Report${availableReportsCount > 1 ? 's' : ''}`;

  function handleAnalysisLogModalClose() {
    setAnalysisLogModalJobID(null);
  }

  const downloadComponent = () => {
    if (isAssetDownloadLoading) return;
    getAssetDownloadOperationId({ variables: { args: { assetId: componentId } } });
  };

  const downloadExtractedComponent = () => {
    if (isExtractedAssetDownloadLoading) return;
    getExtractedAssetDownloadOperationId({ variables: { args: { assetId: componentId } } });
  };

  useEffect(() => {
    if (assetDownload) {
      setDownloadTask({ downloadUrl: assetDownload, isExtracted: false });
    }
  }, [assetDownload]);

  useEffect(() => {
    if (extractedAssetDownload) {
      setDownloadTask({ downloadUrl: extractedAssetDownload, isExtracted: true });
    }
  }, [extractedAssetDownload]);

  const handleManageGroupsClick = () => {
    setSelectedAsset(effectiveComponent?.row || effectiveComponent);
    setDisableEdit(false);
    setShowManageGroupsModal(true);
    handleMenuClose();
  };

  const handleDeleteGroupsClick = () => {
    setGroupsModalContent({ content: <RemoveFromGroupModal assetId={componentId} groupId={assetGroupId} />, title: 'Remove from Group' });
    handleMenuClose();
  };

  useEffect(() => {
    async function asyncFun() {
      if (!!downloadTask?.downloadUrl) {
        if (tasks.find(e => e.downloadUrl === downloadTask.downloadUrl && e.status === CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.IN_PROGRESS)) {
          enqueueSnackbar(<CustomError message={CONSTANTS.DOWNLOAD.ALREADY_IN_PROGRESS_MESSAGE} />, {
            variant: 'warning',
            preventDuplicate: true
          });
          return;
        }
        setTasks(prev => [
          {
            downloadUrl: downloadTask.downloadUrl,
            fileName: `${effectiveComponent?.name || effectiveComponent?.component?.name}${downloadTask.isExtracted ? '_extracted.tar.gz' : ''}`,
            status: CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.CREATED,
            type: CONSTANTS.TASKS.TYPE.DOWNLOAD,
            id: uuidv4()
          },
          ...prev
        ]);
      }
    }
    asyncFun();
  }, [downloadTask]);

  return (
    <div>
      <NRMenu
        id="demo-customized-menu"
        MenuListProps={{
          'aria-labelledby': componentId ? `asset-actions-button-${componentId}` : 'actions-button'
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
      >
        {!!handleEditClick && (
          <WithPermissions permission={CONSTANTS.PERMISSIONS.WRITE_IMGS} resourceType={ACR_RESOURCE_TYPE.ASSET} resourceId={assetResourceId}>
            <MenuItem
              id="asset-action-edit"
              disabled={disableActions}
              onClick={handleEditClick}
              disableRipple
              className={clsx(classes.item, classes.firstItem)}
            >
              <ModeEditOutlineOutlinedIcon />
              Edit Asset Details
            </MenuItem>
          </WithPermissions>
        )}
        <WithPermissions
          rbacPermission={RBAC_PERMISSIONS.WRITE_ASSET_GROUPS}
          permission={CONSTANTS.PERMISSIONS.WRITE_GROUPS}
          resourceType={ACR_RESOURCE_TYPE.ORGANIZATION}
        >
          <MenuItem
            id="asset-action-manage-groups"
            disabled={disableActions}
            onClick={handleManageGroupsClick}
            disableRipple
            className={clsx(classes.item, classes.groupsIcon, { [classes.firstItem]: !handleEditClick })}
          >
            <NRGroupIcon width={'24'} height={'24'} />
            <Typography sx={{ pl: 1.5 }}>Manage Groups</Typography>
          </MenuItem>
        </WithPermissions>
        <MenuItem id="asset-action-analysis-log" onClick={handleViewAnalysisLogClick} disableRipple className={classes.item}>
          <ArticleOutlinedIcon />
          View Analysis Log
        </MenuItem>
        <WithPermissions
          permission={CONSTANTS.PERMISSIONS.READ_IMGS}
          rbacPermission={RBAC_PERMISSIONS.FINDINGS_EXPORT}
          resourceType={ACR_RESOURCE_TYPE.ASSET}
          resourceId={assetResourceId}
        >
          <MenuItem id="asset-action-generate-pdf" disabled={disableActions} onClick={handlePdfGeneration} disableRipple className={classes.item}>
            {/* <DescriptionOutlinedIcon /> */}
            {!isGenerating && <DescriptionOutlinedIcon />}
            {isGenerating && <CircularProgress className={classes.downloadLoading} size={20} />}
            {pdfReportText}
          </MenuItem>
        </WithPermissions>
        <WithPermissions
          permission={CONSTANTS.PERMISSIONS.READ_IMGS}
          rbacPermission={RBAC_PERMISSIONS.DOWNLOAD_ASSETS}
          resourceType={ACR_RESOURCE_TYPE.ASSET}
          resourceId={assetResourceId}
        >
          <MenuItem
            id="asset-action-download-asset"
            disabled={disableActions}
            onClick={() => downloadComponent()}
            disableRipple
            className={classes.item}
          >
            {!isAssetDownloadLoading && <FileDownloadOutlinedIcon />}
            {isAssetDownloadLoading && <CircularProgress className={classes.downloadLoading} size={20} />}
            Download Asset
          </MenuItem>
          <MenuItem
            id="asset-action-download-extracted"
            disabled={disableActions}
            onClick={() => downloadExtractedComponent()}
            disableRipple
            className={classes.item}
          >
            {!isExtractedAssetDownloadLoading && <FileDownloadOutlinedIcon />}
            {isExtractedAssetDownloadLoading && <CircularProgress className={classes.downloadLoading} size={20} />}
            Download Extracted Asset
          </MenuItem>
        </WithPermissions>
        {!handleEditClick && (
          <MenuItem disabled={disableActions} onClick={handlePivotToSearch} disableRipple className={classes.item}>
            <PageviewOutlinedIcon />
            Pivot To Search
          </MenuItem>
        )}
        <>
          {location?.pathname === '/groups' && <Divider sx={{ my: 0.5 }} />}
          {location?.pathname === '/groups' && (
            <WithPermissions
              rbacPermission={RBAC_PERMISSIONS.WRITE_ASSET_GROUPS}
              permission={CONSTANTS.PERMISSIONS.WRITE_GROUPS}
              resourceType={ACR_RESOURCE_TYPE.ASSET_GROUP}
              resourceId={assetGroupId}
            >
              <MenuItem
                id="asset-action-remove-from-group"
                disabled={disableActions}
                onClick={handleDeleteGroupsClick}
                disableRipple
                className={classes.item}
              >
                <HighlightOffOutlinedIcon />
                Remove From Group
              </MenuItem>
            </WithPermissions>
          )}
        </>
        {/* <Divider sx={{ my: 0.5 }} /> */}
        {/* <MenuItem onClick={handleMenuClose} disableRipple className={clsx(classes.item, classes.lastItem)}>
              <DeleteOutlinedIcon />
              Delete Asset
            </MenuItem> */}
      </NRMenu>
      <NRAnalysisLog handleClose={handleAnalysisLogModalClose} open={!!analysisLogModalJobID} componentId={analysisLogModalJobID} />
      <NRActionModal
        title={pdfReportText}
        content={
          <PDFReportSelectionModal
            isAssetSummarySelected={isAssetSummarySelected}
            isPQCReadinessSelected={isPQCReadinessSelected}
            isRiseAISelected={isRiseAISelected}
            onAssetSummaryToggle={handleAssetSummaryToggle}
            onPQCReadinessToggle={handlePQCReadinessToggle}
            onRiseAIToggle={handleRiseAIToggle}
            isGenerating={isGenerating}
            isRiseAIInsightsReportEnabled={isRiseAIInsightsReportEnabled && caasAvailability}
            isPQCReadinessReportEnabled={true}
            onGenerate={handlePdfReportGenerate}
            onCancel={handlePdfReportModalClose}
            hasSelectedReports={hasSelectedReports}
          />
        }
        open={pdfReportModalOpen}
        handleClose={handlePdfReportModalClose}
        width={500}
      />
      <NRAssetSummaryPrintableContainer
        assetId={componentId}
        assetName={assetName}
        generateToken={assetSummaryGenerateToken}
        onComplete={() => {
          setAssetSummaryGenerateToken(null);
          setIsGenerating(false);
          handlePdfReportModalClose();
        }}
        onError={() => {
          setAssetSummaryGenerateToken(null);
          setIsGenerating(false);
          enqueueSnackbar('Failed to generate PDF. Please try again later.', {
            variant: 'error'
          });
        }}
      />
    </div>
  );
}
export default NRAssetsActions;
